// チャット履歴のグローバル管理
class ChatHistoryManager {
  constructor() {
    this.storageKey = 'alsok_chatHistory'
    this.listeners = new Set()
    this.chatHistory = this.loadChatHistory()
    
    // localStorage変更イベントを監視（別タブでの変更を検知）
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey && e.newValue) {
        console.log('📡 ChatHistoryManager: 別タブでの履歴変更を検知')
        this.handleStorageChange(e.newValue)
      }
    })
  }

  // ローカルストレージから履歴を読み込み
  loadChatHistory() {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // timestampをDateオブジェクトに変換
        return parsed.map(chat => ({
          ...chat,
          timestamp: new Date(chat.timestamp)
        }))
      }
    } catch (error) {
      console.error('チャット履歴の読み込みエラー:', error)
    }
    return []
  }

  // localStorage変更時の処理（別タブでの変更を同期）
  handleStorageChange(newValueString) {
    try {
      const newHistory = JSON.parse(newValueString)
      const parsedHistory = newHistory.map(chat => ({
        ...chat,
        timestamp: new Date(chat.timestamp)
      }))
      
      // 内部履歴を更新
      this.chatHistory = parsedHistory
      
      // 全てのリスナーに通知
      this.notifyListeners({
        type: 'historyUpdatedFromStorage',
        history: this.getChatHistory()
      })
    } catch (error) {
      console.error('別タブでの履歴同期エラー:', error)
    }
  }

  // ローカルストレージに保存
  saveChatHistory() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.chatHistory))
    } catch (error) {
      console.error('チャット履歴の保存エラー:', error)
    }
  }

  // 現在の履歴を取得
  getChatHistory() {
    return [...this.chatHistory]
  }

  // チャットメッセージを追加（タイムスタンプはすでに設定済みと仮定）
  addMessage(message) {
    const newMessage = {
      type: message.type || 'ai',
      message: message.message || '',
      timestamp: message.timestamp || new Date()
    }
    
    console.log('📝 ChatHistoryManager: メッセージを追加:', newMessage.message, 'リスナー数:', this.listeners.size)
    
    this.chatHistory.push(newMessage)
    this.saveChatHistory()
    
    // 全てのリスナーに通知
    this.notifyListeners({
      type: 'messageAdded',
      message: newMessage,
      history: this.getChatHistory()
    })
  }

  // 複数のメッセージを一度に追加（タイムスタンプはすでに設定済みと仮定）
  addMessages(messages) {
    const newMessages = messages.map(msg => ({
      type: msg.type || 'ai',
      message: msg.message || '',
      timestamp: msg.timestamp || new Date()
    }))
    
    this.chatHistory.push(...newMessages)
    this.saveChatHistory()
    
    // 全てのリスナーに通知
    this.notifyListeners({
      type: 'messagesAdded',
      messages: newMessages,
      history: this.getChatHistory()
    })
  }

  // 履歴をクリア
  clearHistory() {
    this.chatHistory = []
    this.saveChatHistory()
    
    this.notifyListeners({
      type: 'historyCleared',
      history: this.getChatHistory()
    })
  }

  // 履歴を初期化（初期メッセージを設定）- 既存の履歴がある場合は初期化しない
  initializeHistory(initialMessage) {
    // 既存の履歴がある場合は初期化しない
    if (this.chatHistory.length > 0) {
      console.log('📋 ChatHistoryManager: 既存の履歴があるため初期化をスキップ')
      return
    }
    
    console.log('🆕 ChatHistoryManager: 履歴を初期化:', initialMessage)
    this.chatHistory = [
      {
        type: 'ai',
        message: initialMessage || 'AI受付システムが起動しました。',
        timestamp: new Date()
      }
    ]
    this.saveChatHistory()
    
    this.notifyListeners({
      type: 'historyInitialized',
      history: this.getChatHistory()
    })
  }

  // イベントリスナーを追加
  addListener(callback) {
    this.listeners.add(callback)
    
    // 削除用の関数を返す
    return () => {
      this.listeners.delete(callback)
    }
  }

  // リスナーに通知
  notifyListeners(event) {
    console.log('📢 ChatHistoryManager: リスナーに通知中:', event.type, 'リスナー数:', this.listeners.size)
    
    this.listeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('チャット履歴リスナーエラー:', error)
      }
    })

    // CustomEventも発火（既存のコードとの互換性のため）
    window.dispatchEvent(new CustomEvent('chatHistoryChanged', { detail: event }))
  }

  // 最新N件のメッセージを取得
  getRecentMessages(limit = 50) {
    return this.chatHistory.slice(-limit)
  }

  // 特定のタイプのメッセージを取得
  getMessagesByType(type) {
    return this.chatHistory.filter(msg => msg.type === type)
  }

  // 本日のメッセージを取得
  getTodayMessages() {
    const today = new Date()
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    
    return this.chatHistory.filter(msg => msg.timestamp >= startOfDay)
  }
}

// シングルトンインスタンス
export const chatHistoryManager = new ChatHistoryManager()